import { useCallback, useEffect } from "react";
import { useBillingMetadata } from "../hooks/useBillingMetadata";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router";
import { Calendar } from "../../SharedModule/components/Calendar";
import { selectBillingCycles } from "../redux/reducers/metaBilling.reducer";
import "../../styles/main.module.scss";
import { AdvancedFilters } from "../components/ProcessBillableItems/AdvancedFilters";
import { FiltersSelect } from "../components/ProcessBillableItems/AdvancedFiltersSelect";
import { Checkbox } from "../../SharedModule/components/Checkbox";
import {
  selectFilters,
  selectEndmonthDate,
  selectMidmonthDate,
  setItem,
  billableItemType,
  toggleSelectedCategory,
  resetFilters as resetFiltersAction,
} from "../redux/reducers/filters.reducer";
import { clear as clearBillableHours } from "../redux/reducers/billableHours.reducer";
import { clearBillableItems } from "../redux/reducers/billableItems.reducer";
import {
  ROUTE_BILLABLE_ITEMS_RESULTS,
  ROUTE_PROFFESSIONAL_SERVICES_QUERY,
} from "../../SharedModule/utils/constants";
import { setActiveTab } from "../../SharedModule/redux/reducers/uiShared.reducer";
import Footer from "../../SharedModule/components/Footer";
import { Spinner } from "../../SharedModule/components/Spinner";
import { billingOwners } from "../../SharedModule/utils/accessUtils";
import { ForbiddenPage } from "../../SharedModule/pages/ForbiddenPage";
import { selectUserRoles } from "../../SharedModule/redux/reducers/auth.reducer";

enum periods {
  MIDMONTH,
  MONTHEND,
}

export const ProfessionalServicesQuery = () => {
  useBillingMetadata();
  const dispatch = useDispatch();
  const billingCycles = useSelector(selectBillingCycles);
  const filtersState = useSelector(selectFilters);

  const navigate = useNavigate();
  const location = useLocation();
  const changePeriod = (period: number) => {
    if (period === periods.MIDMONTH) {
      dispatch(selectMidmonthDate());
    } else {
      dispatch(selectEndmonthDate());
    }
  };

  const resetFilters = useCallback(
    (event: any) => {
      event?.preventDefault();
      dispatch(resetFiltersAction());
    },
    [dispatch]
  );

  const { userRolesList, rolesLoaded } = useSelector(selectUserRoles);

  useEffect(() => {
    document.title = "The Hub - Process Professional Services";
  }, []);

  useEffect(() => {
    if (location.hash !== "#keep") {
      resetFilters(null);
    }

    dispatch(setActiveTab(ROUTE_PROFFESSIONAL_SERVICES_QUERY));
  }, [location.hash, resetFilters]);

  const isDisabled = filtersState.selectedCategories.length <= 0;

  const runQuery = async (event: any) => {
    event.preventDefault();
    if (isDisabled) return;
    dispatch(clearBillableHours());
    dispatch(clearBillableItems());
    navigate(ROUTE_BILLABLE_ITEMS_RESULTS, {
      state: { from: ROUTE_PROFFESSIONAL_SERVICES_QUERY },
    });
  };

  return (
    <>
      {!rolesLoaded && (
        <Spinner
          style={{
            marginLeft: "50%",
            marginTop: "5%",
          }}
        />
      )}

      {rolesLoaded && !billingOwners(userRolesList) && <ForbiddenPage />}

      {rolesLoaded && billingOwners(userRolesList) && (
        <div className="content">
          <div className="content-header" style={{ marginTop: "0" }}>
            <h2>Process Professional Services</h2>
          </div>
          <form className="card invoice-summary-background p-4 mt-3">
            <div className="row justify-content-center">
              <div className="col-sm-3 col-form-label">Billing period</div>
              <div className="col-sm-5">
                <div className="d-flex">
                  <div className="position-relative">
                    <label htmlFor="ps-date-from" className="form-label">
                      From
                    </label>
                    <Calendar
                      id="ps-date-from"
                      date={filtersState.dateFrom}
                      onChange={(date: string) =>
                        dispatch(setItem({ dateFrom: date.toString() }))
                      }
                    />
                  </div>
                  <div className="position-relative ms-5">
                    <label htmlFor="ps-date-to" className="form-label">
                      To
                    </label>
                    <Calendar
                      id="ps-date-to"
                      date={filtersState.dateTo}
                      onChange={(date: string) =>
                        dispatch(setItem({ dateTo: date.toString() }))
                      }
                    />
                  </div>
                </div>
                <div className="mt-2">
                  <div
                    className="btn button-secondary"
                    role="button"
                    tabIndex={0}
                    onClick={() => changePeriod(periods.MIDMONTH)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        changePeriod(periods.MIDMONTH);
                      }
                    }}
                  >
                    Mid Month
                  </div>
                  <div
                    className="btn button-secondary ms-2"
                    role="button"
                    tabIndex={0}
                    onClick={() => changePeriod(periods.MONTHEND)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        changePeriod(periods.MONTHEND);
                      }
                    }}
                  >
                    Month's End
                  </div>
                </div>
              </div>
            </div>
            <FiltersSelect
              className="row justify-content-center mt-4"
              label="Billing cycles"
              options={billingCycles}
              value={filtersState.billingCycles}
              isLoading={!billingCycles}
              onChange={(billingCycle) => {
                // Needs to be converted to null instead of setting an empty array
                dispatch(
                  setItem({
                    billingCycles: billingCycle.length ? billingCycle : null,
                  })
                );
              }}
            />
            <div className="row justify-content-center mt-4">
              <div className="col-sm-3 col-form-label">Include</div>
              <div className="col-sm-5">
                <Checkbox
                  label="Billable hours"
                  value={
                    filtersState.selectedCategories.indexOf(
                      billableItemType.billableHours
                    ) !== -1
                  }
                  onClickHandler={() =>
                    dispatch(
                      toggleSelectedCategory(billableItemType.billableHours)
                    )
                  }
                />
                <Checkbox
                  className="mt-2"
                  label="Resources"
                  value={
                    filtersState.selectedCategories.indexOf(
                      billableItemType.resources
                    ) !== -1
                  }
                  onClickHandler={() =>
                    dispatch(toggleSelectedCategory(billableItemType.resources))
                  }
                />
                <Checkbox
                  className="mt-2"
                  label="Billing milestones"
                  value={
                    filtersState.selectedCategories.indexOf(
                      billableItemType.billingMilestones
                    ) !== -1
                  }
                  onClickHandler={() =>
                    dispatch(
                      toggleSelectedCategory(billableItemType.billingMilestones)
                    )
                  }
                />
              </div>
            </div>
            <div className="row justify-content-center mt-4">
              <div className="col-sm-3 col-form-label">Other</div>
              <div className="col-sm-5">
                <Checkbox
                  label="Include work orders attached to a project."
                  value={filtersState.includeWO}
                  onClickHandler={() =>
                    dispatch(setItem({ includeWO: !filtersState.includeWO }))
                  }
                />
              </div>
            </div>
            <AdvancedFilters />
            <hr className="separator" />
            <div className="mt-4 footer-filter">
              <div
                className="link-text"
                role="button"
                tabIndex={0}
                onClick={resetFilters}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    resetFilters(e);
                  }
                }}
              >
                Reset Filters
              </div>
              <button
                className={`btn btn-primary ${isDisabled ? "disabled" : ""}`}
                onClick={runQuery}
              >
                Run Query
              </button>
            </div>
          </form>
          <Footer />
        </div>
      )}
    </>
  );
};
